import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { Card } from "reactstrap";
import { useBeManualFetcher } from "../../../hooks/useBeManualFetcher";
import { useStudentPageContext } from "../../../hooks/useStudentPageContext";
import { studentService } from "../../../services/studentService";
import EventEmitter, { EVENT_TOPICS } from "../../../utilities/eventUtils";
import { appendToFormData } from "../../../utilities/formDataUtils";
import BlockUpdateButton from "../../button/BlockUpdateButton";

import { Block, BlockBetween, BlockTitle } from "../../Component";
import UpdatableText from "../../shared/UpdatabaleText";

const StudentAddressInfoBlock = () => {
  const { id } = useParams();
  const { register, handleSubmit } = useForm();
  const [onFetch] = useBeManualFetcher();
  const { student } = useStudentPageContext();

  const [hasEditClicked, setHasEditClicked] = useState(false);

  function handleOnEditClicked() {
    setHasEditClicked((prev) => !prev);
  }

  const hasTemporaryAddress =
    student?.sameAsPermanentAddress != null ||
    student?.temporaryAddressCity ||
    student?.temporaryAddressDistrict ||
    student?.temporaryAddressStreet ||
    student?.temporaryAddressPostalCode != null ||
    student?.temporaryAddressInstruction;

  async function onFormSubmit(form) {
    const { city, district, street, instruction, postalCode, country } = form;
    const newFormData = new FormData();

    appendToFormData(newFormData, {
      city,
      district,
      street,
      instruction,
      postalCode,
      country,
    });

    return await onFetch({
      action: () => studentService.updateStudent(id, newFormData),
      onLoad: (response) => {
        EventEmitter.emit(EVENT_TOPICS.ON_STUDENT_UPDATED, { response });
        handleOnEditClicked();
      },
      successMessage: "Student fields updated successfully",
    });
  }

  return (
    <Card className="card-inner">
      <Block>
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <BlockBetween>
            <BlockTitle tag="h5">Address Information</BlockTitle>
            <BlockUpdateButton
              hasEditClicked={hasEditClicked}
              handleOnEditClicked={handleOnEditClicked}
            />
          </BlockBetween>
          <div className="profile-ud-list">
            <div className="profile-ud-item  mr-5">
              <UpdatableText
                title="City"
                defaultValue={student?.address?.city}
                name={"city"}
                register={register}
                onEditClicked={hasEditClicked}
                isWider
              />
              <UpdatableText
                title="District"
                defaultValue={student?.address?.district}
                name={"district"}
                register={register}
                onEditClicked={hasEditClicked}
                isWider
              />
              <UpdatableText
                title="Street"
                defaultValue={student?.address?.street}
                name={"street"}
                register={register}
                onEditClicked={hasEditClicked}
                isWider
              />
            </div>
            <div className="profile-ud-item">
              <UpdatableText
                title="Home Number"
                defaultValue={student?.address?.instruction}
                name={"instruction"}
                register={register}
                onEditClicked={hasEditClicked}
                isWider
              />
              <UpdatableText
                title="Postal Code"
                defaultValue={student?.address?.postalCode}
                name={"postalCode"}
                register={register}
                onEditClicked={hasEditClicked}
                isWider
              />
              <UpdatableText
                title="Country"
                defaultValue={student?.country}
                name={"country"}
                register={register}
                onEditClicked={hasEditClicked}
                isWider
              />
            </div>
          </div>
          {hasTemporaryAddress && (
            <>
              <BlockTitle tag="h6" className="mt-3">
                Temporary Address
              </BlockTitle>
              <div className="profile-ud-list">
                <div className="profile-ud-item mr-5">
                  {student?.sameAsPermanentAddress != null && (
                    <UpdatableText
                      title="Same as Permanent Address"
                      defaultValue={
                        student?.sameAsPermanentAddress ? "Yes" : "No"
                      }
                      name={"sameAsPermanentAddress"}
                      isWider
                    />
                  )}
                  {student?.temporaryAddressCity && (
                    <UpdatableText
                      title="City"
                      defaultValue={student?.temporaryAddressCity}
                      name={"temporaryAddressCity"}
                      isWider
                    />
                  )}
                  {student?.temporaryAddressDistrict && (
                    <UpdatableText
                      title="District"
                      defaultValue={student?.temporaryAddressDistrict}
                      name={"temporaryAddressDistrict"}
                      isWider
                    />
                  )}
                  {student?.temporaryAddressStreet && (
                    <UpdatableText
                      title="Street"
                      defaultValue={student?.temporaryAddressStreet}
                      name={"temporaryAddressStreet"}
                      isWider
                    />
                  )}
                </div>
                <div className="profile-ud-item">
                  {student?.temporaryAddressPostalCode != null && (
                    <UpdatableText
                      title="Postal Code"
                      defaultValue={student?.temporaryAddressPostalCode}
                      name={"temporaryAddressPostalCode"}
                      isWider
                    />
                  )}
                  {student?.temporaryAddressInstruction && (
                    <UpdatableText
                      title="Home Number"
                      defaultValue={student?.temporaryAddressInstruction}
                      name={"temporaryAddressInstruction"}
                      isWider
                    />
                  )}
                </div>
              </div>
            </>
          )}
        </form>
      </Block>
    </Card>
  );
};

export default StudentAddressInfoBlock;
